/* eslint-disable max-lines */
import HistoryIcon from '@mui/icons-material/History';
import {
  Box,
  Grid,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import visuallyHidden from '@mui/utils/visuallyHidden';
import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import BusinessAddressIndicator from 'common/components/BusinessAddressIndicator';
import ShippingTimelineModal from 'common/modules/ShippingTimelineModal';
import { format } from 'date-fns';
import { capitalize, find, includes, isBoolean, isEmpty, isNil, map, toLower } from 'lodash';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import itemProcessingState from 'common/constants/itemState';
import orderStatus from 'common/constants/orderStatus';
import useRoles from 'common/hooks/useRoles';
import BatchShipmentDialog from 'common/modules/BatchShipmentDialog';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import { useGetPartnerAccountName } from 'common/utils';
import { updateBatchShipmentFilter } from 'store/thunks/batchShipmentThunk';
import { getOrdersByPage, sortOrdersList } from 'store/thunks/ordersListThunk';

const headCells = [
  { id: 'orderId', label: 'Order Id' },
  { id: 'referenceId', label: 'Reference Id' },
  { id: 'partnerName', label: 'Partner Name' },
  { id: 'partnerAccount', label: 'Partner Account' },
  { id: 'patientName', label: 'Patient' },
  { id: 'status', label: 'Status' },
  { id: 'isOtc', label: 'Type' },
  { id: 'rxNumber', label: 'Rx Number' },
  { id: 'processingState', label: 'Item State' },
  { id: 'shippingOption', label: 'Shipping Option' },
  { id: 'createdAt', label: 'Created At' },
  { id: 'updatedAt', label: 'Updated At' },
  { id: 'batchShipmentId', label: 'Batch Shipment Id' },
];

const sortableColumns = [
  'orderId',
  'referenceId',
  'patientName',
  'rxNumber',
  'createdAt',
  'updatedAt',
  'partnerAccount',
];

const OrdersTable = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sortBy, pages, currentPage } = useSelector(({ ordersList }) => ordersList);
  const totalPages = useSelector(({ ordersList }) => Math.ceil(ordersList.count / 25));
  const orders = pages[currentPage];
  const [openDialog, setOpenDialog] = useState(false);
  const [currentBatchShipmentId, setBatchShipmentId] = useState(null);
  const [timelineOrderId, setTimelineOrderId] = useState(null);
  const shippingOptions = useSelector(({ systemConfigs }) => systemConfigs?.shippingOptions || []);
  const hasAccess = useRoles();

  const partnerAccountHandler = useGetPartnerAccountName();

  const handleClose = () => {
    setBatchShipmentId(null);
    setOpenDialog(false);
  };

  const getPartnerAccountName = (partnerAccountId) => {
    return partnerAccountId && partnerAccountHandler[partnerAccountId]
      ? capitalize(partnerAccountHandler[partnerAccountId].partnerAccountName)
      : 'N/A';
  };

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;

    dispatch(
      sortOrdersList({
        sortBy: {
          sortKey,
          sortOrder: sortBy.sortKey === sortKey && sortBy.sortOrder === 'ASC' ? 'DESC' : 'ASC',
        },
      })
    );
  };

  const handleOpenDialog = (batchShipmentId) => {
    setBatchShipmentId(batchShipmentId);
    setOpenDialog(true);
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) {
      // page didnt change, don't do anything.
      return;
    }

    dispatch(getOrdersByPage({ page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToBatchShipmentQueue = (orderId) => {
    dispatch(
      updateBatchShipmentFilter({
        filter: { orderId },
      })
    );
    navigate('/batchShipments');
  };

  return (
    <Grid component={Paper} sx={{ py: 1, px: 2 }} container direction='column'>
      <BatchShipmentDialog
        batchShipmentId={currentBatchShipmentId}
        onClose={handleClose}
        open={openDialog}
      />
      <Grid item>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={
                      sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : false
                    }
                  >
                    {!includes(sortableColumns, headCell.id) ? (
                      headCell.label
                    ) : (
                      <TableSortLabel
                        active={sortBy.sortKey === headCell.id}
                        direction={
                          sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : 'asc'
                        }
                        onClick={handleSort}
                        data-head-property={headCell.id}
                      >
                        {headCell.label}
                        {sortBy.sortKey === headCell.id ? (
                          <Box component='span' sx={visuallyHidden}>
                            {toLower(sortBy.sortOrder) === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {map(orders, (order) => (
                <TableRow key={order.lineItemId} hover>
                  <TableCell>
                    <Link
                      to={`/orders/${order.orderId}`}
                      style={{ color: theme.palette.text.primary }}
                    >
                      {order.orderId}
                    </Link>
                  </TableCell>
                  <TableCell>{order.referenceId || 'N/A'}</TableCell>
                  <TableCell>{order.partnerName || 'N/A'}</TableCell>
                  <TableCell>{getPartnerAccountName(order.partnerAccountId)}</TableCell>
                  <TableCell component='th' scope='row' align='left'>
                    <Link
                      to={`/patients/${order.mpi}`}
                      style={{ color: theme.palette.text.primary }}
                    >
                      {order.patientName}
                    </Link>
                  </TableCell>
                  <TableCell>{orderStatus[order.status]}</TableCell>
                  <TableCell>{isBoolean(order.isOtc) && order.isOtc ? 'OTC' : 'RX'}</TableCell>
                  <TableCell>{order.rxNumber}</TableCell>
                  <TableCell>{itemProcessingState[order.processingState]}</TableCell>
                  <TableCell>
                    {order.shippingOption
                      ? find(
                          shippingOptions,
                          (option) => option.optionCode === order.shippingOption
                        )?.shippingOption || order.shippingOption
                      : 'N/A'}
                  </TableCell>
                  <TableCell>{format(new Date(order.createdAt), 'MM/dd/yyyy')}</TableCell>
                  <TableCell>{format(new Date(order.updatedAt), 'MM/dd/yyyy')}</TableCell>
                  {order.batchShipmentId && (
                    <TableCell
                      sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => handleOpenDialog(order.batchShipmentId)}
                    >
                      {order.batchShipmentId}
                    </TableCell>
                  )}
                  {!order.batchShipmentId && <TableCell>N/A</TableCell>}
                  <TableCell align='center'>
                    <Tooltip title='View Shipment History' arrow placement='top'>
                      <IconButton
                        size='small'
                        onClick={() => setTimelineOrderId(order.orderId)}
                        sx={{ color: 'primary.main' }}
                      >
                        <HistoryIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align='center'>
                    {order.batchShipmentId && (
                      <Tooltip
                        title={batchShippingTooltip({
                          isBusinessAddress: order.isBusinessAddress,
                          isShipped: true,
                          isClickable: true,
                        })}
                        arrow
                        placement='top'
                      >
                        <BatchShippingIcon
                          color={perfectRxTheme.palette.success.main}
                          onClick={() => handleOpenDialog(order.batchShipmentId)}
                          showBadge={order.isBusinessAddress}
                        />
                      </Tooltip>
                    )}
                    {order.inBatchShipping &&
                      !order.batchShipmentId &&
                      order.processingState !== 'TRANSFERRED' && (
                        <Tooltip
                          title={
                            order.inBatchShipping
                              ? batchShippingTooltip({
                                  isBusinessAddress: order.isBusinessAddress,
                                  isClickable: hasAccess.viewBatchShipments,
                                })
                              : ''
                          }
                          arrow
                          placement='top'
                        >
                          <BatchShippingIcon
                            onClick={
                              hasAccess.viewBatchShipments
                                ? () => navigateToBatchShipmentQueue(order.orderId)
                                : undefined
                            }
                            color={perfectRxTheme.palette.warning.warningColour}
                            showBadge={order.isBusinessAddress}
                          />
                        </Tooltip>
                      )}
                    {!order.inBatchShipping &&
                      (!order.batchShipmentId && order.isBusinessAddress ? (
                        <BusinessAddressIndicator />
                      ) : (
                        <Grid />
                      ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            {!isNil(orders) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={15} align='right' sx={{ borderBottom: 'none' }}>
                    <Pagination
                      sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(orders) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(orders) && isEmpty(orders) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
      <ShippingTimelineModal
        open={!!timelineOrderId}
        onClose={() => setTimelineOrderId(null)}
        orderId={timelineOrderId}
      />
    </Grid>
  );
};

export default OrdersTable;
